import { createI18n } from "vue-i18n";
import { localStorageName } from "@/config/utils";
import en from "./locales/en.json";
import zh from "./locales/zh.json";
import ja from "./locales/ja.json";
import ko from "./locales/ko.json";
import th from "./locales/th.json";

// 5 supported languages. Each ships a static UI-string bundle below; item
// NAMES are auto-translated per locale at runtime via menuTranslate.js.
export const SUPPORTED_LOCALES = ["en", "zh", "ja", "ko", "th"];
export const DEFAULT_LOCALE = "en";
// Namespaced per merchant+branch by localStorageName(). Distinct from the
// legacy presentational "MOO_LOCALE" key the top bar used before i18n existed.
const LOCALE_STORAGE_KEY = "MOO_ACTIVE_LOCALE";

// Bilingual "pairs": the app always displays ONE language at a time, toggled
// (via the top-bar pill) between English and the second language of the pair
// the customer picked. Only the second language code is persisted; the active
// locale is always within {en, second}.
const PAIR_STORAGE_KEY = "MOO_LANG_PAIR";
export const DEFAULT_PAIR = "zh"; // ENG/CHN by default
// Labels are foreign-language-first ("Chn/Eng"-style) since English is the
// secondary language of every pair.
export const LANGUAGE_PAIRS = [
  { second: "zh", label: "Chn/Eng" },
  { second: "ja", label: "Jap/Eng" },
  { second: "th", label: "Thai/Eng" },
  { second: "ko", label: "Kor/Eng" },
];

// Open in English by default. fallbackLocale stays English so any missing
// translation key still resolves to English rather than showing the raw key.
const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  messages: { en, zh, ja, ko, th },
});

// Read the persisted locale for the current merchant+branch. Returns
// DEFAULT_LOCALE when nothing is saved or the saved value is unsupported.
// Safe to call before merchant/branch are populated — localStorageName just
// produces a namespace with empty segments.
export function readPersistedLocale() {
  try {
    const stored = localStorage.getItem(localStorageName(LOCALE_STORAGE_KEY));
    if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
  } catch (e) {
    // ignore storage errors
  }
  return DEFAULT_LOCALE;
}

// Apply a locale to the i18n instance + persist it. Falls back to
// DEFAULT_LOCALE for unsupported values so a stale key can't lock the UI.
export function setLocale(locale) {
  const next = SUPPORTED_LOCALES.includes(locale) ? locale : DEFAULT_LOCALE;
  i18n.global.locale.value = next;
  try {
    localStorage.setItem(localStorageName(LOCALE_STORAGE_KEY), next);
  } catch (e) {
    // ignore storage errors
  }
  return next;
}

// Read the persisted language pair (the second/non-English locale). Falls
// back to DEFAULT_PAIR when unset or invalid.
export function readLanguagePair() {
  try {
    const stored = localStorage.getItem(localStorageName(PAIR_STORAGE_KEY));
    if (stored && stored !== "en" && SUPPORTED_LOCALES.includes(stored)) {
      return stored;
    }
  } catch (e) {
    // ignore storage errors
  }
  return DEFAULT_PAIR;
}

// Persist the chosen pair (second language). If a non-English language is
// currently displayed, switch it to the new second language so the change
// applies immediately; otherwise stay on English. Keeps the active locale
// within {en, second}.
export function setLanguagePair(second) {
  const next =
    second !== "en" && SUPPORTED_LOCALES.includes(second)
      ? second
      : DEFAULT_PAIR;
  try {
    localStorage.setItem(localStorageName(PAIR_STORAGE_KEY), next);
  } catch (e) {
    // ignore storage errors
  }
  if (i18n.global.locale.value !== "en") {
    setLocale(next);
  }
  return next;
}

// Toggle between English and the pair's second language. Used by the top-bar
// pill; wrapping the swap here keeps each parent view from knowing the pair.
export function toggleLocale() {
  const second = readLanguagePair();
  const current = i18n.global.locale.value;
  return setLocale(current === "en" ? second : "en");
}

// Restore the persisted locale into the i18n instance. Call from App.vue once
// merchant/branch codes are known so the namespaced key resolves. Clamps to
// {en, pair-second} so a locale chosen before the pair changed can't leave the
// UI showing a language outside the active pair.
export function restoreLocale() {
  const persisted = readPersistedLocale();
  const second = readLanguagePair();
  const next = persisted === "en" || persisted === second ? persisted : "en";
  i18n.global.locale.value = next;
  return next;
}

// Kiosk (KSK054) picks any of the 5 languages straight from the Landing list —
// no English pair — so it keeps its own key; the MOO pair clamp in
// restoreLocale() would otherwise drop e.g. "ja" back to English on reload.
const KIOSK_LOCALE_STORAGE_KEY = "KSK_ACTIVE_LOCALE";

export function setKioskLocale(locale) {
  const next = SUPPORTED_LOCALES.includes(locale) ? locale : DEFAULT_LOCALE;
  i18n.global.locale.value = next;
  try {
    localStorage.setItem(localStorageName(KIOSK_LOCALE_STORAGE_KEY), next);
  } catch (e) {
    // ignore storage errors
  }
  return next;
}

export function restoreKioskLocale() {
  let next = DEFAULT_LOCALE;
  try {
    const stored = localStorage.getItem(localStorageName(KIOSK_LOCALE_STORAGE_KEY));
    if (stored && SUPPORTED_LOCALES.includes(stored)) next = stored;
  } catch (e) {
    // ignore storage errors
  }
  i18n.global.locale.value = next;
  return next;
}

export default i18n;
